 
import type { ChartType, Plugin } from 'chart.js';
import type { Chart } from 'chart.js';
import { ThemeColors } from 'core/utilities/theme';
import isEqual from 'react-fast-compare';

/** The selection is only enabled if one of the following key is pressed (or none) */
const enabledKeysPressed = ['Control', 'Shift', undefined];
let keyPressed: string | undefined;

let timer: ReturnType<typeof setTimeout>;

window.addEventListener('keydown', (e) => {
  if (timer) clearTimeout(timer);
  keyPressed = e.key;
});

window.addEventListener('keyup', () => {
  timer = setTimeout(() => {
    keyPressed = undefined;
  }, 300);
});

// Ensure we clear the state when the window tab lose focus as 'keyup' is not called when using alt+tab
window.onblur = () => {
  keyPressed = undefined;
};

/** Enabled when the user is dragging a new selection box */
let selecting = false;

interface SelectionBounds {
  fromX: number;
  toX: number;
  fromY: number;
  toY: number;
}

function newSelectionBounds(): SelectionBounds {
  return {
    fromX: 0, toX: 0, fromY: 0, toY: 0,
  };
}

let selectionBounds: SelectionBounds = newSelectionBounds();

/** Double events are sent, we want to ensure we only run one */
const lastMousedownPosition = { x: 0, y: 0 };

let tempSelectionBounds = newSelectionBounds();

function drawSelectionRectangle(ctx: CanvasRenderingContext2D) {
  if (isEqual(tempSelectionBounds, newSelectionBounds())) return;

  ctx.save();
  ctx.beginPath();
  ctx.fillRect(
    tempSelectionBounds.fromX,
    tempSelectionBounds.fromY,
    tempSelectionBounds.toX - tempSelectionBounds.fromX,
    tempSelectionBounds.toY - tempSelectionBounds.fromY,
  );
  ctx.fillStyle = ThemeColors.dataAnalyzerSelection;
  ctx.lineWidth = 1;
  ctx.strokeStyle = ThemeColors.dataAnalyzerSelectionBorder;
  ctx.strokeRect(
    tempSelectionBounds.fromX,
    tempSelectionBounds.fromY,
    tempSelectionBounds.toX - tempSelectionBounds.fromX,
    tempSelectionBounds.toY - tempSelectionBounds.fromY,
  );
}

function enableChartInteraction(chart: Chart) {
  if (!chart.config.options) return;

  if (!chart.config.options?.interaction) chart.config.options.interaction = { mode: 'nearest' };
  if (chart.config.options?.interaction) {
    chart.config.options.interaction.mode = 'nearest';
    chart.update();
  }
}

function disableChartInteraction(chart: Chart) {
  if (!chart.config.options) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!chart.config.options?.interaction) chart.config.options.interaction = { mode: 'none' as any };
  if (chart.config.options?.interaction) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    chart.config.options.interaction.mode = 'none' as any;
    chart.update();
  }
}

export interface ChartSelection {
  bounds: SelectionBounds;
  ctrlKey: boolean;
  shiftKey: boolean;
}

interface ChartJSPluginSelection {
  name: string;
  onSelect: (selection?: ChartSelection) => void;
}

declare module 'chart.js' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType extends ChartType> {
    selection: ChartJSPluginSelection;
  }
}

const chartJSSelectionPlugin: Plugin = {
  id: 'selection',
  beforeTooltipDraw() {
    return !selecting;
  },
  afterDraw(chart) {
    if (selecting) {
      drawSelectionRectangle(chart.ctx);
    }
  },
  afterEvent: (chart, e) => {
    if (!chart.options.plugins?.selection?.onSelect || !enabledKeysPressed.includes(keyPressed)) {
      return;
    }

    if (e.event.native?.type === 'mouseout' && e.event.x && e.event.y) {
      tempSelectionBounds = newSelectionBounds();
      selectionBounds = newSelectionBounds();

      selecting = false;

      // Re-draw chart to remove the selection rectangle
      chart.draw();
    }

    if (
      e.event.native?.type === 'mousedown'
      && !(e.event.native as MouseEvent)?.altKey
      && e.event.x && e.event.y
      && lastMousedownPosition.x !== e.event.x && lastMousedownPosition.y !== e.event.y
    ) {
      lastMousedownPosition.x = e.event.x;
      lastMousedownPosition.y = e.event.y;

      selectionBounds = {
        fromX: e.event.x, toX: 0, fromY: e.event.y, toY: 0,
      };

      disableChartInteraction(chart);
      selecting = true;

      // Disable points hover size while selecting (helps with rendering speed)
      chart.data.datasets.forEach((dataset) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (dataset as any).pointHoverRadius = (dataset as any).pointHoverRadiusWhileSelecting;
      });
    }

    if (e.event.native?.type === 'mouseup') {
      if (selecting && e.event.x && e.event.y) {
        enableChartInteraction(chart);
        selecting = false;

        if (selectionBounds.fromX === e.event.x && selectionBounds.fromY === e.event.y) {
          chart.options.plugins.selection.onSelect();
        } else {
          if (selectionBounds.fromX > e.event.x) {
            selectionBounds.toX = selectionBounds.fromX;
            selectionBounds.fromX = e.event.x;
          } else {
            selectionBounds.toX = e.event.x;
          }

          if (selectionBounds.fromY < e.event.y) {
            selectionBounds.toY = selectionBounds.fromY;
            selectionBounds.fromY = e.event.y;
          } else {
            selectionBounds.toY = e.event.y;
          }

          const bounds: SelectionBounds = {
            fromX: chart.scales.x.getValueForPixel(selectionBounds.fromX) as number,
            toX: chart.scales.x.getValueForPixel(selectionBounds.toX) as number,
            fromY: chart.scales.y.getValueForPixel(selectionBounds.fromY) as number,
            toY: chart.scales.y.getValueForPixel(selectionBounds.toY) as number,
          };

          // Changing axis direction will return opposite bounds
          if (bounds.fromX > bounds.toX) {
            const boundsFromX = bounds.fromX;
            bounds.fromX = bounds.toX;
            bounds.toX = boundsFromX;
          }

          if (bounds.fromY > bounds.toY) {
            const boundsFromY = bounds.fromY;
            bounds.fromY = bounds.toY;
            bounds.toY = boundsFromY;
          }

          chart.options.plugins.selection.onSelect({
            bounds,
            ctrlKey: (e.event.native as MouseEvent).ctrlKey,
            shiftKey: (e.event.native as MouseEvent).shiftKey,
          });
        }
      }

      tempSelectionBounds = newSelectionBounds();
      selectionBounds = newSelectionBounds();
    }

    if (
      e.event.native?.type === 'mousemove'
      && selecting
      && e.event.x && e.event.y
      && selectionBounds.fromX !== e.event.x && selectionBounds.fromY !== e.event.y
      && !e.changed
    ) {
      chart.draw(); // redraw the chart to remove the selection rectangle

      // Set temp bounds that will be used to draw the selection rectangle in afterDraw
      tempSelectionBounds = newSelectionBounds();

      if (selectionBounds.fromX > e.event.x) {
        tempSelectionBounds.toX = selectionBounds.fromX;
        tempSelectionBounds.fromX = e.event.x;
      } else {
        tempSelectionBounds.toX = e.event.x;
        tempSelectionBounds.fromX = selectionBounds.fromX;
      }

      if (selectionBounds.fromY > e.event.y) {
        tempSelectionBounds.toY = selectionBounds.fromY;
        tempSelectionBounds.fromY = e.event.y;
      } else {
        tempSelectionBounds.toY = e.event.y;
        tempSelectionBounds.fromY = selectionBounds.fromY;
      }
    }
  },
};

export default chartJSSelectionPlugin;
